/**
 * InfoTooltip.jsx - Phase 115: Contextual Help Tooltips
 * 
 * A standardized tooltip component for explaining complex UI elements
 * throughout the BioDataStudio platform.
 * 
 * Features:
 * - Consistent styling with subtle info icon
 * - Supports rich content (title + description)
 * - Accessible with keyboard navigation
 * - Predefined tooltips for common metrics
 */
import { Info, HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Centralized tooltip definitions for reuse across the app.
 * Keys match common metric names for easy lookup.
 * Current navigation: Overview, Markets, Research, Deal Flow, Monitor
 */
export const TOOLTIP_DEFINITIONS = {
  // Financial Metrics
  runway: {
    title: "Cash Runway",
    description: "Estimated months of cash coverage using FMP cash and short-term investments divided by annualized quarterly operating cash outflow. Statements must match in quarter and currency. Financing and future spending can change this estimate.",
    learnMore: null
  },
  burn_rate: {
    title: "Burn Rate",
    description: "Estimated annual operating cash outflow from FMP: the latest negative quarterly operating cash flow multiplied by four. Check the quarter-end date. Unavailable for companies without matching source statements; it is not a spending forecast.",
    learnMore: null
  },
  market_cap: {
    title: "Market Capitalization",
    description: "Total market value of the company's outstanding shares. Calculated as share price × shares outstanding.",
    learnMore: null
  },
  
  // Clinical Metrics
  phase: {
    title: "Clinical Phase",
    description: "Registered study phase describes the development stage. Phase alone does not establish successful results, approval or current activity; check the study status and source record.",
    learnMore: null
  },
  trial_count: {
    title: "Active Trials",
    description: "Count of matching ClinicalTrials.gov registrations within the displayed scope and status filters. Check coverage and source dates; a registration is not a clinical result.",
    learnMore: null
  },
  
  // Opportunity Signals
  opportunity_signal: {
    title: "Opportunity Signal",
    description: "A reported or derived development worth investigating. Check its supporting source and assumptions before treating it as a commercial opportunity.",
    learnMore: null
  },
  patent_cliff: {
    title: "Patent Cliff",
    description: "A reported patent or exclusivity expiry relevant to a product. Expiry alone does not establish loss of all protection, generic entry or revenue impact.",
    learnMore: null
  },
  
  // Sentiment & News
  sentiment: {
    title: "Sentiment Analysis",
    description: "Headline tone is classified as Positive, Negative, or Neutral. The sample shows its recorded AI model or keyword-estimate method. Tone does not predict stock performance.",
    learnMore: null
  },
  news_volume: {
    title: "News Volume",
    description: "Count of retrieved articles in the displayed sample period. Source availability and filtering affect coverage; article volume does not measure importance.",
    learnMore: null
  },
  
  // Watchlist & Tracking
  watchlist_health: {
    title: "Watchlist Health",
    description: "Displayed status of tracked subjects within the available data. Review source-linked updates and dates; following a subject does not generate a report or create an alert.",
    learnMore: null
  },
  
  // Market evidence metrics
  tam: {
    title: "Total Addressable Market (TAM)",
    description: "A market-demand estimate where supported. Inspect the stated population, pricing, geography, date and calculation; it is not a measured revenue total.",
    learnMore: null
  },
  prevalence: {
    title: "Disease Prevalence",
    description: "People living with a condition within a stated population and period. Check whether the source reports a count, percentage or rate; prevalence alone does not determine commercial opportunity.",
    learnMore: null
  },
  confidence: {
    title: "Confidence Level",
    description: "A report-specific indicator. Read its stated method, available sources and missing fields; a high label does not guarantee that a conclusion is correct.",
    learnMore: null
  },
  competitive_intensity: {
    title: "Competitive Intensity",
    description: "Matching companies or programs within the selected scope. Check participation roles and coverage; counts do not establish ownership, pricing pressure or a complete market.",
    learnMore: null
  },
  patent_expiry: {
    title: "Patent Expiry Timeline",
    description: "Reported expiry dates for listed patents or regulatory exclusivity. These are distinct protections; dates alone do not predict generic entry, litigation outcomes or revenue change.",
    learnMore: null
  },
  
  // Subscription
  credits: {
    title: "Pro Subscription",
    description: "Check your account and the current plan or purchase screen for access, available credits and any charge before starting a paid action.",
    learnMore: null
  },
  
  // Orange Book
  orange_book: {
    title: "FDA Orange Book",
    description: "FDA-listed approved drug products with therapeutic equivalence evaluations, listed patents and exclusivity. The Orange Book does not cover every biologic or every relevant patent.",
    learnMore: null
  },
  
  // Company Status
  public_private: {
    title: "Public/Private Status",
    description: "Public companies trade on stock exchanges (NYSE, NASDAQ) with disclosed financials. Private companies have limited public data.",
    learnMore: null
  },
  
  // Navigation Tooltips
  overview: {
    title: "Overview",
    description: "Your command center. See market activity, tracked items, and quick access to all platform features.",
    learnMore: null
  },
  research: {
    title: "Research",
    description: "Search companies and assets in one field. Refine matches without clearing your query; choose ambiguous names or spelling suggestions. Report evidence coverage varies.",
    learnMore: null
  },
  landscape: {
    title: "Markets",
    description: "Resolve a supported therapeutic scope and explicitly run a fresh report. Compare retained treatment, trial and company evidence; check source dates and coverage limits.",
    learnMore: null
  },
  monitor: {
    title: "Monitor",
    description: "Read source-linked industry updates and follow companies, assets and supported market scopes in named lists. Following does not refresh saved research or create alerts.",
    learnMore: null
  },

  // Private Market Radar
  radar_company_age: {
    title: "Reported Incorporation",
    description: "Reported incorporation year is not the company founding date or evidence of stealth status.",
    learnMore: null
  },
  radar_offering: {
    title: "Offering Amount",
    description: "Amount offered as disclosed in Form D. It is distinct from amount sold and does not establish cash raised or cash on hand. Missing and indefinite values remain separate.",
    learnMore: null
  },
  radar_clinical: {
    title: "Clinical Evidence",
    description: "Verified lead-sponsor matches link to ClinicalTrials.gov registrations. Highest phase may include historical studies. No verified match does not establish preclinical status.",
    learnMore: null
  },
  radar_filed: {
    title: "Filing Date",
    description: "Date the Form D was filed with the SEC. This is when the company disclosed its fundraise — the actual raise may have started earlier.",
    learnMore: null
  },
  radar_stealth: {
    title: "Reported Incorporation",
    description: "Incorporation recency does not establish stealth status, revenue, announcements or financing stage.",
    learnMore: null
  },
  radar_equity_rounds: {
    title: "Security Types",
    description: "Equity and debt are filing checkboxes. Both being selected does not establish a convertible instrument or a distinct financing round.",
    learnMore: null
  },
  radar_clinical_matches: {
    title: "Verified Trial Matches",
    description: "Issuers with verified lead-sponsor matches to ClinicalTrials.gov registrations. Registration is not proof of results or scientific validation.",
    learnMore: null
  },
  radar_total_capital: {
    title: "Disclosed Offering Amount",
    description: "Sum of numeric offering amounts in the latest saved filing per issuer. This is not total capital raised or a complete financing history. Missing and indefinite amounts are excluded.",
    learnMore: null
  },
  radar_heatmap: {
    title: "Sector Exploration",
    description: "Ranked bars is the default; Treemap is the alternate. Bar length and tile area reflect the selected offering amount, sold amount or issuer count. Colors identify sectors. Zero and missing values for the selected metric have no tile, but the full sector list retains their filter controls. Missing is not zero.",
    learnMore: null
  },
  radar_sector_trends: {
    title: "Sector Activity Context",
    description: "Counts compare latest saved issuer filings within the stated periods. They do not represent a complete financing history. Amendments and cumulative amount sold must not be summed as new capital raises.",
    learnMore: null
  },
};

/**
 * InfoTooltip Component
 * 
 * A small info icon that shows helpful context on hover.
 * 
 * @param {string} tooltipKey - Key from TOOLTIP_DEFINITIONS for predefined content
 * @param {string} title - Custom title (overrides tooltipKey)
 * @param {string} description - Custom description (overrides tooltipKey)
 * @param {string} side - Tooltip position: "top" | "right" | "bottom" | "left"
 * @param {string} size - Icon size: "sm" | "md" | "lg"
 * @param {string} variant - Visual style: "subtle" | "default"
 * @param {ReactNode} children - Custom trigger element (instead of info icon)
 */
export default function InfoTooltip({
  tooltipKey,
  title,
  description,
  side = "top",
  size = "sm",
  variant = "subtle",
  className = "",
  children
}) {
  // Get predefined content if tooltipKey provided
  const predefined = tooltipKey ? TOOLTIP_DEFINITIONS[tooltipKey] : null;
  const finalTitle = title || predefined?.title;
  const finalDescription = description || predefined?.description;
  
  if (!finalTitle && !finalDescription) {
    return null;
  }
  
  const iconSize = size === "sm" ? 12 : size === "md" ? 14 : 16;
  const iconClass = variant === "subtle" 
    ? "text-slate-400 hover:text-slate-600" 
    : "text-slate-500 hover:text-slate-700";
  
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children || (
            <button
              type="button"
              className={`inline-flex items-center justify-center cursor-help transition-colors ${iconClass} ${className}`}
              data-testid={`info-tooltip-${tooltipKey || 'custom'}`}
            >
              <Info size={iconSize} />
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-[280px] bg-slate-900 text-white p-3 rounded-sm shadow-sm border border-slate-700"
        >
          {finalTitle && (
            <div className="font-medium text-sm mb-1">{finalTitle}</div>
          )}
          {finalDescription && (
            <div className="text-xs text-slate-300 leading-relaxed">
              {finalDescription}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * MetricWithTooltip Component
 * 
 * Wraps a metric value with an info tooltip for context.
 * Commonly used for financial metrics, trial counts, etc.
 * 
 * @param {string} label - Metric label
 * @param {string|number} value - Metric value to display
 * @param {string} tooltipKey - Key from TOOLTIP_DEFINITIONS
 * @param {string} valueClassName - Custom class for value styling
 */
export function MetricWithTooltip({
  label,
  value,
  tooltipKey,
  title,
  description,
  valueClassName = "",
  labelClassName = ""
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-slate-500 ${labelClassName}`}>{label}</span>
      <InfoTooltip tooltipKey={tooltipKey} title={title} description={description} />
      {value !== undefined && (
        <span className={`font-medium ${valueClassName}`}>{value}</span>
      )}
    </div>
  );
}

/**
 * LabelWithTooltip Component
 * 
 * A label with an inline info icon for definitions.
 * Used for section headers and form labels.
 */
export function LabelWithTooltip({
  label,
  tooltipKey,
  title,
  description,
  className = ""
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      {label}
      <InfoTooltip tooltipKey={tooltipKey} title={title} description={description} />
    </span>
  );
}
